import { AUTH_STRINGS } from "../const";

/**
 * True when the device reports itself as offline. This is the only signal the
 * browser exposes to JavaScript about connectivity — the specific
 * `net::ERR_*` code shown in the console is NOT available to fetch/JS.
 *
 * @returns {boolean}
 */
const isOffline = () =>
	typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Detects a connection/network-layer failure (the browser fetch rejecting with
 * a TypeError, Amplify's "Network error", the device being offline, etc.) as
 * opposed to an actual response coming back from Cognito.
 *
 * @param {unknown} error Error thrown by an Amplify Auth call
 * @returns {boolean}
 */
const isNetworkError = (error) => {
	if (!error) {
		return false;
	}
	if (isOffline()) {
		return true;
	}
	return (
		error.name === "TypeError" ||
		error.code === "NetworkError" ||
		error.message === "Network error" ||
		error.message === "Failed to fetch"
	);
};

/**
 * Builds a short technical suffix for support triage, e.g.
 * " (details: TypeError: Failed to fetch)".
 *
 * NOTE: the browser does not expose the underlying `net::ERR_*` code (e.g.
 * ERR_INTERNET_DISCONNECTED) to JavaScript, so this reflects only what the
 * rejected error object actually carries — typically the generic
 * "TypeError: Failed to fetch".
 *
 * @param {unknown} error Error thrown by an Amplify Auth call
 * @returns {string}
 */
const technicalDetail = (error) => {
	const parts = [error?.name, error?.message].filter(Boolean);
	return parts.length ? ` (details: ${parts.join(": ")})` : "";
};

/**
 * Maps an error thrown by an AWS Amplify Auth call to a user-facing message.
 *
 * - Offline / unreachable server return a clear connectivity message (with a
 *   technical detail suffix for support) instead of a misleading domain error
 *   such as "Please provide a valid email address".
 * - Errors carrying a message from Cognito return that message unchanged.
 * - Anything without a usable message returns the provided fallback.
 *
 * @param {unknown} error Error thrown by an Amplify Auth call
 * @param {string} [fallback] Message to use when the error has no message
 * @returns {string}
 */
export const getErrorMessage = (
	error,
	fallback = AUTH_STRINGS.common.errorMessage.generic
) => {
	if (isNetworkError(error)) {
		const base = isOffline()
			? AUTH_STRINGS.common.errorMessage.offline
			: AUTH_STRINGS.common.errorMessage.serverUnreachable;
		return `${base}${technicalDetail(error)}`;
	}
	return error?.message || fallback;
};
